'use client';
import { TrackPromotionImpression } from '@components/TrackPromotionImpression/TrackPromotionImpression';
import { usePromoTrackingEvents } from '@components/TrackPromotionImpression/usePromoTrackingEvents';
import { useCookieSettings } from '@lib/hooks/useCookieSettings';
import { HomepageHero as HomepageHeroType } from '@lib/types/cms/HomepageHero';
import { isDefined } from '@lib/util/isDefined';
import { toNonNullable } from '@lib/util/toNonNullable';
import { useRouter } from 'next/navigation';
import { ButtonStyle, HomepageHero as HomepageHeroUI } from 'ui-components';
import { useHeroCountdown } from './useHeroCountdown';

type HomepageHeroProps = {
  data: HomepageHeroType;
  position: number;
};

const HomepageHero = ({ data, position }: HomepageHeroProps) => {
  const { functionalCategoryEnabled, targetingCategoryEnabled } =
    useCookieSettings();
  const hasCookieConsent =
    targetingCategoryEnabled && functionalCategoryEnabled;

  const router = useRouter();
  const onExpiredCountdown = () => {
    router.refresh();
  };
  const { expired, hasCountdown, countdown } = useHeroCountdown(
    data.countdownTo ?? null,
    onExpiredCountdown
  );

  const showCountdown = hasCountdown && !expired;

  const { promoClickTrackingEvent, promoViewTrackingEvent } =
    usePromoTrackingEvents({
      title: data.titleInternal || '',
      typename: 'HomepageHero',
      position,
      creativeTitle: toNonNullable(data.image?.title || data.video?.title),
      trackAsPromo: data.trackAsPromo ?? false,
      ctas: data.ctas.map((cta) => cta.label).filter(isDefined),
    });

  const actions = data.ctas.map((cta) => ({
    label: cta.label,
    url: cta.url,
    openInNewTab: toNonNullable(cta.openInNewTab),
    style:
      cta.appearance === 'primary'
        ? ('primary' as ButtonStyle)
        : ('link' as ButtonStyle),
  }));

  return (
    <TrackPromotionImpression
      onImpression={promoViewTrackingEvent}
      shouldTrack={data.trackAsPromo ?? false}
    >
      <HomepageHeroUI
        subHeader={data.subheader}
        title={data.heading}
        image={{
          url: data.image?.url ?? '',
          alt: data.image?.alt ?? '',
          height: toNonNullable(data.image?.height),
          width: toNonNullable(data.image?.width),
        }}
        imageFocusArea={data.imageFocusArea}
        video={
          data.video?.url
            ? {
                url: data.video.url,
                title: data.video.title ?? '',
                description: data.video.description ?? '',
                fallbackImage: data.video.fallbackImageUrl ?? '',
              }
            : undefined
        }
        embeddedVideo={
          data.embeddedVideo?.url
            ? {
                url: data.embeddedVideo.url,
                fallbackImage: data.embeddedVideo.fallbackImageUrl ?? '',
                title: data.embeddedVideo.title ?? '',
              }
            : undefined
        }
        hasCookieConsent={hasCookieConsent}
        description={data.description ?? ''}
        countdownDuration={showCountdown ? countdown : undefined}
        countdownLabels={data.countdownLabels ?? {}}
        actions={actions}
        align={data.alignment ?? 'left'}
        onCtaClick={promoClickTrackingEvent}
      />
    </TrackPromotionImpression>
  );
};

export default HomepageHero;
